import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { BaseFilterParams } from 'dashboard/lib/interfaces/BaseFilterParams';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { CaseQueue } from 'dashboard/models/case-queue';
import { PaginationParams } from 'lib/interfaces/api';

export interface CaseQueuesFilterParams extends BaseFilterParams {
  status?: ('active' | 'archived')[];
}

export interface CaseQueuesState extends TableState {
  caseQueues: CaseQueue[];

  // Overriding type declared in TableState
  filters: CaseQueuesFilterParams;

  atActiveCaseQueueLimit: boolean;
}

export const initialCaseQueuesState: CaseQueuesState = {
  caseQueues: [],
  currentPage: 1,
  filters: { status: ['active'] },
  pageSize: DEFAULT_PAGE_SIZE,
  sortParams: {
    sort: 'updated_at',
    shouldSortDescending: true,
  },
  paginationParams: undefined,
  atActiveCaseQueueLimit: true,
};

interface SetCaseQueuesPayload {
  caseQueues: CaseQueue[];
  atActiveCaseQueueLimit: boolean;
  paginationParams?: PaginationParams;
}

interface SetFiltersPayload {
  filters: CaseQueuesFilterParams;
}
interface SetSortParamsPayload {
  sortParams: SortParams;
}
interface SetCurrentPagePayload {
  currentPage: number;
}
interface SetPageSizePayload {
  pageSize: number;
}

const { reducer, actions } = createSlice({
  name: 'case',
  initialState: initialCaseQueuesState,
  reducers: {
    clearCaseQueues: () => {
      return initialCaseQueuesState;
    },
    setCaseQueues: (state, action: PayloadAction<SetCaseQueuesPayload>) => {
      state.caseQueues = action.payload.caseQueues;
      state.atActiveCaseQueueLimit = action.payload.atActiveCaseQueueLimit;
      state.paginationParams = action.payload.paginationParams;
    },
    setFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };
      return isEqual(newFilters, state.filters)
        ? state
        : {
            ...state,
            filters: newFilters,
          };
    },
    setSortParams: (state, action: PayloadAction<SetSortParamsPayload>) => {
      return {
        ...state,
        sortParams: {
          ...action.payload.sortParams,
        },
      };
    },
    setCurrentPage: (state, action: PayloadAction<SetCurrentPagePayload>) => {
      state.currentPage = action.payload.currentPage;
    },
    setPageSize: (state, action: PayloadAction<SetPageSizePayload>) => {
      state.pageSize = action.payload.pageSize;
    },
  },
});

export const {
  clearCaseQueues,
  setCaseQueues,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} = actions;

export const caseQueuesReducer = reducer;
